import React from "react";
import Link from "next/link";

import { CALocations, USLocations } from "constants/dealLocations";
import { getDomainFromContext } from "hooks/useDomain";

import ExpandableContainer from "components/botanic/ExpandableContainer";
import { DispensaryLocationResponse } from "pages/dispensaries/locations";

const LocationLink: React.FC<{
  url: string;
  className?: string;
  location: { slug?: string; name: string };
}> = ({ url, location, className }) => (
  <div
    key={location?.slug || url}
    className={className}
    data-testid={`${location.slug || location.name.toLowerCase()}-link`}
  >
    <Link
      href={location?.slug ? `/deals/${location.slug}` : url}
      legacyBehavior
    >
      {location.name}
    </Link>
  </div>
);

const LocationsByCountry: React.FC<{
  country: string;
  countryLocations: {
    name: string;
    url?: string;
    locations: { url?: string; slug?: string; name: string }[];
  }[];
  dispensaryLocationsPage?: boolean;
}> = ({ country, countryLocations, dispensaryLocationsPage }) => {
  const baseUrl = getDomainFromContext(country === "Canada" ? "CA" : "US");
  return (
    <div className="mt-xxl">
      <h2 className="mb-md">{country}</h2>
      <div className="grid gap-lg grid-cols-2 md:grid-cols-4">
        {countryLocations.map((state) => (
          <div key={state.name}>
            {dispensaryLocationsPage ? (
              <LocationLink
                key={state.name}
                location={{
                  name: state.name,
                }}
                className="font-bold text-lg mb-md"
                url={`${baseUrl}${state.url}`}
              />
            ) : (
              <h3 className="mb-md">{state.name}</h3>
            )}
            {state.locations.length >= 6 ? (
              <ExpandableContainer initialHeight="144px">
                {state.locations.map((location) => (
                  <LocationLink
                    key={location?.slug || location?.url}
                    location={location}
                    url={`${baseUrl}${location?.url}`}
                  />
                ))}
              </ExpandableContainer>
            ) : (
              <div>
                {state.locations.map((location) => (
                  <LocationLink
                    key={location?.slug || location?.url}
                    location={location}
                    url={`${baseUrl}${location?.url}`}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const DealLocations: React.FC<{
  dispensaryLocationsPage?: DispensaryLocationResponse[];
}> = ({ dispensaryLocationsPage }) => (
  <>
    {dispensaryLocationsPage ? (
      dispensaryLocationsPage.map(({ country, countryLocations }) => (
        <LocationsByCountry
          key={country}
          country={country}
          countryLocations={countryLocations}
          dispensaryLocationsPage
        />
      ))
    ) : (
      <>
        <LocationsByCountry
          country="United States"
          countryLocations={USLocations}
        />
        <LocationsByCountry country="Canada" countryLocations={CALocations} />
      </>
    )}
  </>
);

export default DealLocations;
